import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { LoadingSpinner, Table, TableActions, TableCard, Wizard } from '~/components';
import { useFeatureFlag } from '~/features/feature-flags/useFeatureFlag';
import { MarketSegmentedControl } from '~/features/shared/market-content';
import { TutorialVideoButton } from '~/features/tutorials/TutorialVideoButton';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import type { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { b2bSideCopy, type ListingSide } from '../../b2bSideCopy';
import { useMsaasListingRef } from '../../marketSelectors';
import { ListingFeeDialog } from '../ListingFeeDialog';
import { CancelListingDialog } from './CancelListingDialog';
import {
  listingLifecycle,
  matchesStatusTab,
  STATUS_TAB_OPTIONS,
  type ListingStatusTab,
} from './listingLifecycle';
import { selectWizardListingSide } from './listingSide';
import { MsaasListingsRefContext } from './msaasProvider';
import { NewListingMenu } from './NewListingMenu';
import { OFFERING_TABLE_COLUMNS, TABLE_COLUMNS } from './tableCols';
import type { Context as MsaasListingContext } from './types';
import { createWizardSteps } from './wizardSteps';

const TAGS = CT.employer.msaasListings;

const SIDE_BY_PARAM: Record<string, ListingSide> = { buying: 'SEEKING', selling: 'OFFERING' };

export const MsaasListingDetails = () => {
  const { loading, msaasListingsRef } = useMsaasListingRef();
  const offeringEnabled = useFeatureFlag('b2bOfferingListings');
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusTab, setStatusTab] = useState<ListingStatusTab>('all');
  const [listingToCancel, setListingToCancel] = useState<number | null>(null);

  const context = useSelector(msaasListingsRef, (s): MsaasListingContext => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return s.context as MsaasListingContext;
    }
    throw new Error('Invalid state: context not found');
  });
  // [MCV-1933] Set while the employer is asked to confirm the listing fee before publishing.
  const feePreview = useSelector(msaasListingsRef, (s) =>
    s?.matches?.('$_FEE_CONFIRM') ? s.context.feePreview : undefined
  );
  const ready = useSelector(
    msaasListingsRef,
    (s) => !!s?.matches?.('$_VIEW') && !s.context.loading && !!s.context.employerUser?.employerId
  );

  // [MCV-1889] "Post what you need" links elsewhere arrive here as ?new=buying|selling, which opens
  // the matching wizard once, then drops the parameter so a reload doesn't open it again.
  const newParam = searchParams.get('new');
  useEffect(() => {
    if (!offeringEnabled || !newParam || !msaasListingsRef || !ready) return;
    const side = SIDE_BY_PARAM[newParam];
    const params = new URLSearchParams(searchParams);
    params.delete('new');
    setSearchParams(params, { replace: true });
    if (side) msaasListingsRef.send({ type: 'ON_OPEN_MODAL', payload: { side } });
  }, [offeringEnabled, newParam, msaasListingsRef, ready, searchParams, setSearchParams]);

  const visibleListings = useMemo(
    () =>
      offeringEnabled
        ? (context?.listings ?? []).filter((row) => matchesStatusTab(row, statusTab))
        : (context?.listings ?? []),
    [offeringEnabled, context?.listings, statusTab]
  );

  if (!msaasListingsRef || !context) return null;

  const { modal, currentStep, listings, listing, deactivating } = context;

  const detail = listing?.detail;
  const wizardSide = selectWizardListingSide(context);

  if (loading) {
    return <LoadingSpinner />;
  }

  const openModal = (side?: ListingSide) => {
    msaasListingsRef.send(
      side ? { type: 'ON_OPEN_MODAL', payload: { side } } : { type: 'ON_OPEN_MODAL' }
    );
  };

  const closeModal = () => {
    msaasListingsRef.send({ type: 'ON_CLOSE_MODAL' });
  };

  // [MCV-1544] Go through the shared confirmation prompt rather than deleting
  // on the table's click. The machine already had the ON_SEND_DELETE_CONFIRMATION
  // path wired (see msaasListing.machine.ts) and Job Listings already used it —
  // B2B listings were the one surface still calling ON_DELETE outright.
  const handleDelete = (id: number) => {
    msaasListingsRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };

  const handleEdit = (id: number) => {
    msaasListingsRef.send({ type: 'ON_EDIT', payload: { id } });
  };

  // [MCV-1587] Before this, the only way to deactivate a listing was the
  // wizard's "Save as Draft" on step 8 — which is why QA couldn't find it.
  // The row action is reversible but still changes search visibility, so make
  // it deliberate through the neutral confirmation prompt before PATCHing.
  const handleDeactivate = (id: number) => {
    msaasListingsRef.send({ type: 'ON_SEND_DEACTIVATE_CONFIRMATION', payload: { id } });
  };

  const handleRelist = (id: number) => {
    msaasListingsRef.send({ type: 'ON_RELIST', payload: { id } });
  };

  const rowActions = (row: MsaasJobListing): TableActions[] => {
    if (deactivating) return [];
    if (offeringEnabled) {
      // Filled is one-way, so a filled or cancelled listing is never edited, paused or deleted:
      // a hand-filled one can be cancelled, a cancelled one can be relisted (DD24, DD25).
      const lifecycle = listingLifecycle(row);
      if (lifecycle === 'FILLED') return [TableActions.cancel];
      if (lifecycle === 'FILLED_BY_CONTRACT') return [];
      if (lifecycle === 'CANCELLED')
        return row.relistedAsListingId == null ? [TableActions.relist] : [];
    }
    // [MCV-1587] Per-row: deactivating an already-inactive listing is a
    // no-op, so the button only appears where it does something.
    return row.activeFlag
      ? [TableActions.edit, TableActions.deactivate, TableActions.delete]
      : [TableActions.edit, TableActions.delete];
  };

  const cancelTarget = listings.find((row) => row.id === listingToCancel);
  const wizardCopy = b2bSideCopy(wizardSide);

  return (
    <MsaasListingsRefContext.Provider value={msaasListingsRef}>
      <TableCard
        title="B2B Listings"
        addLabel="Create New Listing"
        onAddClick={() => openModal()}
        addBtnTag={TAGS.table.createBtn}
        addAccessory={<TutorialVideoButton videoId="b2bListing" tooltipAlign="start-then-end" />}
        addControl={
          offeringEnabled ? (
            <NewListingMenu onSelect={openModal} buttonTag={TAGS.table.createBtn} />
          ) : undefined
        }
        layoutTag={TAGS.table.layout}
      >
        {offeringEnabled && (
          <MarketSegmentedControl
            className="mb-4"
            options={STATUS_TAB_OPTIONS}
            value={statusTab}
            onChange={setStatusTab}
          />
        )}
        <Table
          actions={(row) => rowActions(row as MsaasJobListing)}
          tags={TAGS.table}
          rowData={visibleListings}
          columns={offeringEnabled ? OFFERING_TABLE_COLUMNS : TABLE_COLUMNS}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDeactivate={handleDeactivate}
          onCancel={setListingToCancel}
          onRelist={handleRelist}
          loading={loading}
        />
      </TableCard>
      <CancelListingDialog
        // A fresh dialog each time, so the relist box never carries over between listings.
        key={listingToCancel ?? 'closed'}
        open={listingToCancel != null}
        listingTitle={cancelTarget?.listingTitle}
        onClose={() => setListingToCancel(null)}
        onConfirm={(relist) => {
          if (listingToCancel != null) {
            msaasListingsRef.send({
              type: 'ON_CANCEL_LISTING',
              payload: { id: listingToCancel, relist },
            });
          }
          setListingToCancel(null);
        }}
      />
      <ModalDialog open={modal.create || modal.edit} onClose={closeModal} xButton>
        {offeringEnabled && (
          <div className="mb-4 flex flex-wrap items-center gap-3 text-left">
            <h2 className="sub-heading-sb text-grey-100" data-cy="msaas-wizard-title">
              {modal.edit ? wizardCopy.editListingTitle : wizardCopy.newListingTitle}
            </h2>
            {modal.edit && (
              <span className="text-sm text-content-secondary" data-cy="msaas-wizard-side-readonly">
                Side: {wizardCopy.sideLabel} (can&apos;t be changed)
              </span>
            )}
          </div>
        )}
        <Wizard
          steps={createWizardSteps(msaasListingsRef, detail, loading, wizardSide)}
          currentStep={currentStep}
        />
      </ModalDialog>
      <ListingFeeDialog
        preview={feePreview ?? null}
        onConfirm={() => msaasListingsRef.send({ type: 'ON_CONFIRM_FEE' })}
        onCancel={() => msaasListingsRef.send({ type: 'ON_CANCEL_FEE' })}
      />
    </MsaasListingsRefContext.Provider>
  );
};
